// [v1.1]: Centralized premium access check
// [PURPOSE]: Single source of truth for premium status — replaces 5+ scattered inline checks
// [HOW]: Combines Firebase Auth premium (Stripe purchase) with dev-mode toggle
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
// [PHASE B / #068 — 2026-05-07]: Canonical dev-build gate. Closes
//   COMING_SOON_ROADMAP §6.1 — replaces inline NODE_ENV check with the
//   single-source-of-truth constant.
import { IS_DEV_BUILD } from '../utils/devMode';

/**
 * [PHASE B / #024 — 2026-05-06]: Loading-aware premium access status.
 *
 * Returns `{ hasPremium, loading }`:
 *   - `hasPremium` mirrors the boolean contract of `usePremiumAccess()`.
 *   - `loading` reflects whether the upstream auth listener has resolved
 *     yet. While `loading === true`, consumers should render a neutral
 *     state (skeleton / spinner / blank) rather than acting on
 *     `hasPremium === false` — that value only carries meaning once
 *     loading has resolved.
 *
 * The flicker bug this addresses: on hard refresh, the AuthProvider
 * mounts with `premiumStatus.isPremium = false` and `loading = true`.
 * The auth listener fires, then `fetchPremiumStatus(uid)` is awaited.
 * During that await window, `useAuth().isPremium` is the initial-state
 * `false`, so consumers that ignore `loading` paint the upgrade prompt.
 * When the fetch resolves, isPremium flips true and the gate swaps to
 * children. Visible flash on every cold load for premium users.
 *
 * Loading-aware consumers (PremiumGate as of #024) read `loading` and
 * suppress the upgrade prompt until the auth state has settled.
 */
export const usePremiumAccessStatus = () => {
  const auth = useAuth();
  const { settings } = useSettings();

  // Defensive: older test mocks may not set `loading`. Treat missing as
  // "not loading" so the hook stays usable in those harnesses.
  const loading = auth.loading === true;
  const isPremium = auth.isPremium === true;

  // Dev-only debug-mode override. Local-only — no Firebase round-trip,
  // safe to surface even while auth is still loading.
  // [PHASE B / #068 — 2026-05-07]: Routed through `IS_DEV_BUILD`.
  const devOverride =
    IS_DEV_BUILD && settings && settings.isPremium === true;

  return {
    hasPremium: isPremium || devOverride,
    loading,
  };
};

/**
 * Returns true if the user has premium access (boolean — backward compat).
 *
 * - Production: only true when Firebase Auth confirms Stripe purchase
 * - Development: also true when settings.isPremium toggle is enabled
 *
 * Consumers that care about the loading window (to avoid the
 * "no premium → premium" flicker on cold refresh) should switch to
 * `usePremiumAccessStatus()` and gate their render on `loading`.
 */
export const usePremiumAccess = () => {
  const { hasPremium } = usePremiumAccessStatus();
  return hasPremium;
};
